import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Stack,
  // Button,
  Typography,
  Tabs,
  Tab,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
// import { ArrowForward, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import SectionTitle from './shared/SectionTitle';
import ProductCard from './shared/ProductCard';
import LoadingSkeleton from './shared/LoadingSkeleton';

const pickLocalized = (value, lang, fallbackLang = 'en') => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value[fallbackLang] || Object.values(value)[0] || '';
};

const CollectionsSection = ({ section, isLoading }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  // const isRTL = i18n.dir() === 'rtl';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const items = useMemo(() => section?.items || [], [section?.items]);
  const configColumns = Number(section?.config?.columns) || 4;
  const columns = isSm ? 2 : isMobile ? 3 : configColumns;
  const showCount = Boolean(section?.config?.show_product_count);

  const [activeTab, setActiveTab] = useState(0);

  if (isLoading) {
    return (
      <Box sx={{ py: 6 }}>
        <Container maxWidth="lg">
          <LoadingSkeleton type="card" count={columns * 2} columns={columns} height={300} />
        </Container>
      </Box>
    );
  }

  if (!items.length) return null;

  const safeIndex = Math.min(activeTab, items.length - 1);
  const activeItem = items[safeIndex];
  const activeCollection = activeItem?.collection || {};
  const activeProducts = activeCollection.products || [];
  // const activeLink =
  //   activeItem?.link_url ||
  //   (activeCollection.slug ? `/collections/${activeCollection.slug}` : null);
  // const activeSubtitle = pickLocalized(activeItem?.subtitle, lang);

  return (
    <Box
      sx={{
        py: { xs: 5, md: 8 },
        position: 'relative',
        background: (th) =>
          th.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${th.palette.background.default} 0%, ${th.palette.background.paper} 100%)`
            : `linear-gradient(180deg, #fff 0%, ${th.palette.grey[50]} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        {/* Main Title */}
        {section?.title && (
          <SectionTitle
            title={section.title}
            subtitle={section.subtitle}
            align="center"
          />
        )}

        {/* Tabs */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: { xs: 3, md: 4 },
          }}
        >
          <Tabs
            value={safeIndex}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="primary"
            indicatorColor="primary"
            sx={{
              maxWidth: '100%',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.875rem', md: '0.95rem' },
                minHeight: 44,
                px: { xs: 2, md: 3 },
                color: 'text.secondary',
                transition: 'color 0.2s',
                '&.Mui-selected': { color: 'primary.main' },
                '&:hover': { color: 'primary.main' },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 3,
              },
            }}
          >
            {items.map((item) => {
              const title =
                pickLocalized(item.title, lang) ||
                pickLocalized(item.collection?.title, lang);
              const count = item.collection?.products?.length || 0;
              return (
                <Tab
                  key={item.id}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>{title}</span>
                      {showCount && count > 0 && (
                        <Chip
                          size="small"
                          label={count}
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: 'action.hover',
                          }}
                        />
                      )}
                    </Stack>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Active collection header row */}
        {/* {(activeSubtitle || activeLink) && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3 }}
          >
            {activeSubtitle ? (
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
                {activeSubtitle}
              </Typography>
            ) : <span />}
            {activeLink && (
              <Button
                onClick={() => navigate(activeLink)}
                variant="outlined"
                endIcon={isRTL ? null : <ArrowForward />}
                startIcon={isRTL ? <ArrowBack /> : null}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 2.5,
                  whiteSpace: 'nowrap',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'transparent',
                  },
                }}
              >
                {t('home.viewCollection', 'View Collection')}
              </Button>
            )}
          </Stack>
        )} */}

        {/* Products Grid — uniform card width across collections via CSS grid */}
        {activeProducts.length > 0 ? (
          <Box
            key={activeItem.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: `repeat(${configColumns}, minmax(0, 1fr))`,
              },
              gap: { xs: 2, md: 3 },
              justifyContent: 'center',
              animation: 'fadeIn 0.4s ease',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {activeProducts.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                onClick={(product) => {
                  if (product.slug) navigate(`/products/${product.slug}`);
                }}
                showActions
                showBadge
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              {t('home.noProductsInCollection', 'No products in this collection yet.')}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CollectionsSection;
